<template>
    <div>
        <div class="action-header">
            <div class="search-container">
                <div class="search-input">
                    <v-text-field
                        class="search-box"
                        v-model="search"
                        prepend-inner-icon="search"
                        :placeholder="this.$vuetify.lang.t('$vuetify.companyHoliday.searchByHolidayName')"
                        solo
                        height="40"
                        @keyup.enter="getHolidaysPagination()">
                    </v-text-field>
                </div>

                <div class="ml-3">
                    <v-btn
                        @click="searchHolidays()"
                        color="primary"
                        outlined
                        height="40">
                        {{ $vuetify.lang.t('$vuetify.companyHoliday.search') }}
                    </v-btn>
                </div>

                <div class="year-input">
                    <v-menu
                        ref="menu"
                        :close-on-content-click="false"
                        v-model="menu"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="290px">
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                slot="activator"
                                v-model="displayYear"
                                prepend-inner-icon="event"
                                outlined
                                @click:prepend-inner="menu = true"
                                v-on="on"
                                height="40"
                                background-color="#ffffff"
                                readonly>
                            </v-text-field>
                        </template>
                        <v-date-picker
                            ref="picker"
                            :locale="localeLang"
                            v-model="year"
                            :max="maxDate"
                            @input="save"
                            reactive
                            no-title>
                        </v-date-picker>
                    </v-menu>
                </div>
            </div>
            <div>
                <v-btn
                    v-if="permissionCreate"
                    @click="openCompanyHolidayForm()"
                    height="40px"
                    color="primary">
                    +{{ $vuetify.lang.t('$vuetify.companyHoliday.createNewHoliday') }}
                </v-btn>
            </div>
        </div>

        <v-card class="content-container">
            <v-data-table
                :footer-props="{
                    'items-per-page-options': [10, 20, 30, 50, 100]
                }"
                @page-count="pageCount = $event"
                :page.sync="page"
                :headers="headers"
                :options.sync="options"
                :server-items-length="totalHoliday"
                :items="holidays"
                :loading="loading">
                <template v-slot:[`item.action`]="{ item }">
                    <div class="action">
                        <v-icon
                            v-if="permissionUpdate && passedDate(item)"
                            class="mr-2"
                            @click="openEditCompanyHolidayModal(item)">
                            edit
                        </v-icon>
                        <v-icon
                            v-if="permissionDelete && passedDate(item)"
                            class="mr-2"
                            @click="openDeleteHolidayModal(item)">
                            delete
                        </v-icon>
                    </div>
                </template>
            </v-data-table>
        </v-card>
        <div class="pt-2 paginator-box">
            <div>
                <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
        </div>

        <company-holiday-form
            ref="companyHolidayForm"
            :updateCompanyHolidayId="updateCompanyHolidayId"
            @success-created="getHolidaysPagination()"
            @success-updated="getHolidaysPagination()"
            @modal-closed="companyFormModalIsClose()"
            :mode="mode"/>

        <delete-modal
            ref="modalDeleteHoliday"
            @success-deleted="deleteSuccess()"
            :deleteForm="deleteForm"/>
    </div>
</template>

<script>
import CompanyHolidayForm from '@/components/modal/CompanyHolidayForm'
import DeleteModal from '@/components/modal/DeleteModal'
import permissionConstant from '@/constants/permissions.js'
export default {
    name: 'CompanyHolidays',

    components: {
        CompanyHolidayForm,
        DeleteModal
    },

    data () {
        return {
            search: '',
            pageCount: 1,
            page: 1,
            options: {},
            loading: false,
            totalHoliday: 0,
            holidays: [],
            deleteForm: {
                header: this.$vuetify.lang.t('$vuetify.companyHoliday.deleteCompanyHoliday'),
                content: '',
                path: ''
            },
            menu: false,
            year: this.$util.getCurrentDate(),
            maxDate: this.$util.getNextYear(2),
            displayYear: this.yearFormat(this.$util.getCurrentDate()),
            mode: '',
            updateCompanyHolidayId: null,
            permissionCreate: false,
            permissionUpdate: false,
            permissionDelete: false
        }
    },

    computed: {
        localeLang () {
            return this.$vuetify.lang.current
        },

        headers () {
            return [
                { text: this.$vuetify.lang.t('$vuetify.companyHoliday.holidayName'), value: 'name', width: '25%' },
                { text: this.$vuetify.lang.t('$vuetify.companyHoliday.holidayDate'), value: 'date', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.companyHoliday.dateCreate'), value: 'created_at', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.companyHoliday.description'), value: 'description', width: '30%' },
                { text: '', value: 'action', width: '15%', sortable: false }
            ]
        }
    },

    methods: {
        async searchHolidays () {
            this.options.page = 1
            await this.getHolidaysPagination()
        },

        async getHolidaysPagination () {
            this.loading = true
            const { sortBy, sortDesc, page, itemsPerPage } = this.options

            const year = this.getYear()

            let url = `company/holidays?page=${page}&itemsPerPage=${itemsPerPage}&year=${year}`

            if (sortBy[0]) {
                url += `&sortBy=${sortBy[0]}`
            }

            if (sortDesc[0]) {
                url += `&sortDesc=${sortDesc[0]}`
            }

            if (this.search) {
                url += `&search=${this.search}`
            }

            const res = await this.axios.get(url)
            if (res && res.data && res.data.status === 'ok') {
                this.holidays = res.data.data
                this.totalHoliday = res.data.total
                this.loading = false
            }
        },

        async save (date) {
            this.$refs.menu.save(date)
            this.$refs.picker.activePicker = 'YEAR'
            this.menu = false

            await this.getHolidaysPagination()
        },

        getYear () {
            const [year] = this.year.split('-')
            return parseInt(year)
        },

        yearFormat (yearPick) {
            const [year] = yearPick.split('-')
            const BEYear = parseInt(year) + 543
            return `${BEYear}`
        },

        openCompanyHolidayForm () {
            this.mode = 'create'
            this.$refs.companyHolidayForm.openModal()
        },

        openEditCompanyHolidayModal (item) {
            this.mode = 'update'
            this.updateCompanyHolidayId = item.id
            this.$refs.companyHolidayForm.openModal()
        },

        companyFormModalIsClose () {
            this.mode = ''
        },

        async deleteSuccess () {
            await this.getHolidaysPagination()
            this.$notify({
                title: this.$vuetify.lang.t('$vuetify.companyHoliday.congratulation'),
                text: this.$vuetify.lang.t('$vuetify.companyHoliday.companyHolidayIsDeleted'),
                type: 'success'
            })
        },

        openDeleteHolidayModal (item) {
            this.deleteForm.content = `${this.$vuetify.lang.t('$vuetify.companyHoliday.wantToDelete')} "${item.name}" ?`
            this.deleteForm.path = `company/holiday/delete/${item.id}`
            this.$refs.modalDeleteHoliday.dialog = true
        },

        passedDate (item) {
            const dateObj = this.$util.dateStringToDateObj(item.date)
            const now = new Date()

            if (now.getTime() > dateObj.getTime()) {
                return false
            }
            return true
        },

        checkPermission () {
            this.permissionCreate = this.$util.checkPermission(permissionConstant.companyHoliday.create)
            this.permissionUpdate = this.$util.checkPermission(permissionConstant.companyHoliday.update)
            this.permissionDelete = this.$util.checkPermission(permissionConstant.companyHoliday.delete)
        }
    },

    mounted () {
        this.checkPermission()
    },

    watch: {
        options: {
            async handler () {
                await this.getHolidaysPagination()
            },
            deep: true
        },
        menu (val) {
            val && this.$nextTick(() => (this.$refs.picker.activePicker = 'YEAR'))
        },
        'year' () {
            this.displayYear = this.yearFormat(this.year)
        }
    }
}
</script>

<style lang="scss" scoped>
::v-deep .v-input__control > .v-input__slot {
    min-height: auto !important;
    display: flex !important;
    align-items: center !important;
}

::v-deep .search-box .v-input__control > .v-input__slot {
    box-shadow: none !important;
    border: 1px solid #a8a8a8 !important;
}

::v-deep .v-input__prepend-inner {
    margin: auto !important;
}

.year-input {
    margin-left: 40px;
}
</style>
